import { useQuery } from "@tanstack/react-query";
import { Eye, EyeOff, FileCheck2, Landmark, Mail, Signature, Users, Import } from "lucide-react";
import { api, unwrap } from "../api/client";
import {
  KIND_LABELS,
  PREVIEW_HINTS,
  PREVIEW_LABELS,
  PROBLEM_HINTS,
  STATUS_LABELS,
  entries,
  homeownerWarning,
  inFlight,
  previewSub,
  statusTone,
  viewsLine,
  when,
  type EmailLike,
} from "../lib/emailActivity";
import { num } from "../lib/format";
import { STATUS_HINTS as PERMIT_HINTS, statusTone as permitTone, STATUS_LABELS as PERMIT_LABELS, timelineLine, type SubmissionStatus } from "../lib/solarapp";
import { EVENT_KEYS as ESIGN_EVENT_KEYS, STATUS_LABELS as ESIGN_LABELS, eventTone as esignTone, isEventKeyed, timelineLine as esignLine, type EventStatus } from "../lib/esign";
import { Alert, Badge } from "./ui";
import { useT, useIntlShape } from "../i18n/I18nProvider";

/** The lead's email delivery and quote-page views (shared by the Lead card's badge and the quote card). */
export function useLeadActivity(orgId: string, publicQuoteId: string | null | undefined) {
  return useQuery({
    queryKey: ["lead-activity", orgId, publicQuoteId],
    enabled: !!publicQuoteId,
    queryFn: () =>
      unwrap(api.GET("/v1/orgs/{org_id}/public-quotes/{quote_id}/activity", { params: { path: { org_id: orgId, quote_id: publicQuoteId! } } })),
    // Follow an email on its way until its delivery report arrives; otherwise look for new views every 30 s
    // (only while the tab is visible).
    refetchInterval: (q) => (q.state.data && inFlight(q.state.data.emails as EmailLike[]) ? 15_000 : 30_000),
  });
}

/** "Bounced" next to the homeowner's address when the latest email to them did not arrive. */
export function EmailProblemBadge({ orgId, publicQuoteId }: { orgId: string; publicQuoteId: string }) {
  const t = useT();
  const activity = useLeadActivity(orgId, publicQuoteId);
  const latest = homeownerWarning((activity.data?.emails ?? []) as EmailLike[]);
  if (!latest) return null;
  const hint = PROBLEM_HINTS[latest.status];
  return (
    <span title={latest.detail ?? (hint ? t(hint) : undefined)}>
      <Badge tone={statusTone(latest.status)}>{t(STATUS_LABELS[latest.status])}</Badge>
    </span>
  );
}

function EmailRow({ e, warned }: { e: EmailLike; warned: boolean }) {
  const t = useT();
  const Icon = e.audience === "company" ? Users : Mail;
  const at = when(e.status_at ?? e.sent_at ?? e.created_at);
  return (
    <li className={e.problem ? "problem" : undefined}>
      <Icon size={14} className="faint" />
      <div className="activity-main">
        <div className="activity-line">
          <span>{t(KIND_LABELS[e.kind])}</span>
          <Badge tone={statusTone(e.status)}>{t(STATUS_LABELS[e.status])}</Badge>
          {at && <span className="small muted">{at}</span>}
        </div>
        <div className="small muted activity-sub">
          {t("designs.activity.to", { recipients: e.to.join(", ") })}
          {e.problem && e.detail && !warned ? <> · {e.detail}</> : null}
        </div>
      </div>
    </li>
  );
}

/** The quote card's "Emails and quote page" section: each email about the lead with its delivery status, the
 * homeowner's views of the quote page, and a warning when a homeowner email bounced or failed. */
export function LeadActivity({ orgId, publicQuoteId }: { orgId: string; publicQuoteId: string }) {
  const t = useT();
  const intl = useIntlShape();
  const activity = useLeadActivity(orgId, publicQuoteId);
  if (!activity.data) return null; // loading or unavailable: the card works without it
  const emails = activity.data.emails as EmailLike[];
  const views = activity.data.views;
  const problem = homeownerWarning(emails);
  const list = entries(emails, views);

  return (
    <div className="lead-activity">
      {problem && (
        <Alert tone="danger" title={t("designs.activity.problemTitle", { kind: t(KIND_LABELS[problem.kind]), status: t(STATUS_LABELS[problem.status]) })}>
          {PROBLEM_HINTS[problem.status] ? t(PROBLEM_HINTS[problem.status]!) : null}
          {problem.detail ? <div className="small activity-reason">{t("designs.activity.reason", { detail: problem.detail })}</div> : null}
        </Alert>
      )}
      <div className="activity-head small muted">{t("designs.activity.title")}</div>
      {list.length === 0 && views.view_count === 0 && !activity.data.permit_submissions?.length && !activity.data.esign_envelopes?.length && !activity.data.notes?.length ? (
        <p className="small muted" style={{ margin: 0 }}>{t("designs.activity.empty")}</p>
      ) : (
        <ul className="activity-list">
          {list.map((item) =>
            item.email ? (
              <EmailRow key={item.key} e={item.email} warned={item.email.id === problem?.id} />
            ) : item.preview ? (
              <li key={item.key} className="activity-preview" title={t(PREVIEW_HINTS[item.preview])}>
                <EyeOff size={14} className="faint" />
                <div className="activity-main">
                  <div className="activity-line">
                    <span className="muted">{t(PREVIEW_LABELS[item.preview])}</span>
                    {when(item.at) && <span className="small muted">{when(item.at)}</span>}
                  </div>
                  {previewSub(item.preview, views, t) && <div className="small muted activity-sub">{previewSub(item.preview, views, t)}</div>}
                </div>
              </li>
            ) : (
              <li key={item.key}>
                <Eye size={14} className="faint" />
                <div className="activity-main">
                  <div className="activity-line">
                    <span>{viewsLine(views, t)}</span>
                  </div>
                </div>
              </li>
            ),
          )}
          {(activity.data.notes ?? []).map((n, i) => (
            <li key={`note-${i}`}>
              <Import size={14} className="faint" />
              <div className="activity-main">
                <div className="activity-line">
                  <span>{n.title}</span>
                  {when(n.at) && <span className="small muted">{when(n.at)}</span>}
                </div>
                {n.detail && <div className="small muted activity-sub">{n.detail}</div>}
              </div>
            </li>
          ))}
          {(activity.data.finance_applications ?? []).map((f, i) => (
            <li key={`finance-${i}`}>
              <Landmark size={14} className="faint" />
              <div className="activity-main">
                <div className="activity-line">
                  <span>{t("designs.activity.finance", { partner: f.partner_name })}</span>
                  {when(f.started_at) && <span className="small muted">{when(f.started_at)}</span>}
                </div>
                {(f.amount != null || f.term_months != null) && (
                  <div className="small muted activity-sub">
                    {[f.amount != null ? t("designs.activity.financeAmount", { amount: num(f.amount) }) : null, f.term_months != null ? t("designs.activity.financeTerm", { count: f.term_months }) : null].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </li>
          ))}
          {(activity.data.permit_submissions ?? []).flatMap((p) =>
            p.history.map((h, i) => {
              const status = h.status as SubmissionStatus;
              const latest = i === p.history.length - 1;
              return (
                <li key={`permit-${p.submission_id}-${i}`}>
                  <FileCheck2 size={14} className="faint" />
                  <div className="activity-main">
                    <div className="activity-line">
                      <span>{timelineLine(status, p.design_name)}</span>
                      {latest && <Badge tone={permitTone(status)}>{PERMIT_LABELS[status]}</Badge>}
                      {when(h.at) && <span className="small muted">{when(h.at)}</span>}
                    </div>
                    <div className="small muted activity-sub">
                      {h.detail ?? PERMIT_HINTS[status]}
                      {latest && status === "approved" && p.permit_number ? ` · ${t("designs.activity.permitNumber", { number: p.permit_number })}` : ""}
                      {latest && p.remote_url ? (
                        <>
                          {" · "}
                          <a href={p.remote_url} target="_blank" rel="noreferrer noopener">{t("designs.activity.openSolarApp")}</a>
                        </>
                      ) : null}
                    </div>
                  </div>
                </li>
              );
            }),
          )}
          {(activity.data.esign_envelopes ?? []).flatMap((env) =>
            env.history.map((h, i) => {
              const status = h.status as EventStatus;
              const latest = i === env.history.length - 1;
              return (
                <li key={`esign-${env.envelope_id}-${i}`} className="activity-esign">
                  <Signature size={14} className="faint" />
                  <div className="activity-main">
                    <div className="activity-line">
                      <span>{isEventKeyed(status) ? t(ESIGN_EVENT_KEYS[status].line, { name: h.recipient ?? "" }) : esignLine({ ...h, status })}</span>
                      <Badge tone={esignTone(status)} plain={!latest}>{isEventKeyed(status) ? t(ESIGN_EVENT_KEYS[status].badge) : ESIGN_LABELS[status]}</Badge>
                      {when(h.at) && <span className="small muted">{when(h.at)}</span>}
                    </div>
                    {(h.detail || (i === 0 && env.signers?.length)) && (
                      <div className="small muted activity-sub">
                        {i === 0 && env.signers?.length ? t("designs.activity.signers", { names: intl.formatList(env.signers, { type: "unit" }) }) : h.detail}
                      </div>
                    )}
                  </div>
                </li>
              );
            }),
          )}
          {views.view_count === 0 && emails.some((e) => e.audience === "homeowner") && (
            <li>
              <Eye size={14} className="faint" />
              <div className="activity-main">
                <div className="activity-line">
                  <span className="muted">{viewsLine(views, t)}</span>
                </div>
              </div>
            </li>
          )}
        </ul>
      )}
      <p className="small faint" style={{ margin: 0 }}>
        {t("designs.activity.footnote")}
      </p>
    </div>
  );
}
